import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { StateUtils, StateWithProcess } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CompanySignUp, CompanySignUpPage, CompanySignUpRequest, ErrorModel, HttpErrorModel, LoaderError } from '../../model';
import { RegistrationNumberInputCheckResult, TaxCodeInputCheckResult } from '../../model/misc.model';
import {
  getProcessErrorFactory,
  getProcessLoadingFactory,
  getProcessStateFactory,
  getProcessSuccessFactory,
} from '../../process/store/selectors/process.selectors';
import { CompanyConnector } from '../connectors/company-connector';
import { CompanyFacade } from '../facades';
import { UserActions } from '../store/actions';
import { LOAD_COMPANY_PROCESS, SEARCH_COMPANY_PROCESS } from '../store/user-state';

@Injectable()
export class CompanyService implements CompanyFacade {
  constructor(
    private store: Store<StateWithProcess<any>>,
    private connector: CompanyConnector
  ) {}

  searchCompany(props: CompanySignUpRequest) {
    this.store.dispatch(new UserActions.SearchCompany(props));
  }

  getSearchCompanyResult(): Observable<CompanySignUpPage | undefined> {
    return this.store.pipe(
      select(getProcessStateFactory<CompanySignUpPage>(SEARCH_COMPANY_PROCESS)),
      map((state: StateUtils.LoaderState<CompanySignUpPage>) => StateUtils.loaderValueSelector<CompanySignUpPage>(state))
    );
  }

  getSearchCompanyResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(SEARCH_COMPANY_PROCESS)));
  }

  getSearchCompanyResultError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(SEARCH_COMPANY_PROCESS)));
  }

  getSearchCompanyResultErrorDetails(): Observable<ErrorModel[] | undefined> {
    return this.getSearchCompanyResultError().pipe(
      map((error) => error as HttpErrorModel),
      map((error: HttpErrorModel) => error?.details)
    );
  }

  getSearchCompanyResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(SEARCH_COMPANY_PROCESS)));
  }

  resetSearchCompanyProcessingState(): void {
    this.store.dispatch(new UserActions.SearchCompanyReset());
  }

  loadCompany(id: string, companySearchServiceType?: string) {
    this.store.dispatch(new UserActions.LoadCompany(id, companySearchServiceType));
  }

  getLoadCompanyResult(): Observable<CompanySignUp | undefined> {
    return this.store.pipe(
      select(getProcessStateFactory<CompanySignUp>(LOAD_COMPANY_PROCESS)),
      map((state: StateUtils.LoaderState<CompanySignUp>) => StateUtils.loaderValueSelector<CompanySignUp>(state))
    );
  }

  getLoadCompanyLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(LOAD_COMPANY_PROCESS)));
  }

  getLoadCompanyError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(LOAD_COMPANY_PROCESS)));
  }

  getLoadCompanySuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(LOAD_COMPANY_PROCESS)));
  }

  validateRegistrationNumber(registrationNumber: string): Observable<RegistrationNumberInputCheckResult> {
    return this.connector.validateRegistrationNumber(registrationNumber);
  }

  validateTaxCode(taxCode: string, countryIsoCode: string): Observable<TaxCodeInputCheckResult> {
    return this.connector.validateTaxCode(taxCode, countryIsoCode);
  }
}
